import { Alert } from 'antd';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { AUTH_CHECKSUM_SEARCH_PARAM_NAME, IdGovProvider } from '@/pages/LoginPage/IdGovProvider';

import { FloatActionButtons } from '@/features/ui';

import { ButtonVariant, ConditionView, Icon, LinkAsButton, Typography } from '@/shared/ui';

import { config } from '@/config/index';

import AuthContext from '../../contexts/Auth/AuthContext';
import { ROUTES } from '../../router/config';

import { BasicProvider } from './BasicProvider';
import { usePageAfterAuth } from './usePageAfterAuth';

import './LoginPage.scss';

const LoginPage: React.FC = () => {
  const { isLoggedIn, storedLinks, userData, handleSessionCheckSum } = useContext(AuthContext);
  const { state } = useLocation();
  const navigate = useNavigate();
  const { pageToOpenAfterAuth } = usePageAfterAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const authChecksum = searchParams.get(AUTH_CHECKSUM_SEARCH_PARAM_NAME);

    if (isLoggedIn && !isEmpty(userData)) {
      navigate(pageToOpenAfterAuth, { replace: true, state });
    } else if (!isEmpty(authChecksum) && !isNull(authChecksum)) {
      handleSessionCheckSum(authChecksum);
    }
  }, [userData, isLoggedIn, pageToOpenAfterAuth, state, navigate, searchParams, handleSessionCheckSum]);

  return (
    <>
      <section className="login-page">
        <div className="container">
          <div className="login-page__header">
            <Icon name="evg_logo" color="blue" width={150} height={50} />
          </div>
          <div className="login-page__form">
            <div className="login-page__form-content">
              <ConditionView if={!!storedLinks?.warnings?.auth}>
                <Alert
                  showIcon
                  className="login-alert"
                  type="warning"
                  message="На жаль, зараз Ви не можете увійти до свого кабінету. Ми знаємо про цю проблему і працюємо над її вирішенням."
                />
              </ConditionView>
              <Typography variant="H3" className="login-page__form-title">
                <ConditionView if={state?.isFromService} else="Авторизуйтесь за допомогою ID.GOV">
                  Для того, щоб замовляти послуги ви маєте авторизуватись
                </ConditionView>
              </Typography>
              <ConditionView if={config.authProviders.basic}>
                <BasicProvider />
              </ConditionView>
              <ConditionView if={config.authProviders.idGov}>
                <IdGovProvider disabled={!config.authProviders.idGov} expectedPath={pageToOpenAfterAuth} />
              </ConditionView>
            </div>
            <div className="login-page__form-footer">
              <LinkAsButton to={ROUTES.HOME} variant={ButtonVariant.GHOST}>
                <Icon name="arrow_back" />
                На головну
              </LinkAsButton>
              <ConditionView if={!!storedLinks.videoInstruction?.authorization}>
                <LinkAsButton to={storedLinks?.videoInstruction?.authorization ?? ''} variant={ButtonVariant.GHOST}>
                  <Icon name="youtube" />
                  Відеоінструкція
                </LinkAsButton>
              </ConditionView>
            </div>
          </div>
        </div>
        <FloatActionButtons />
      </section>
    </>
  );
};

export default LoginPage;
